<template>
    <KonvaCampaignImage
        v-if="image"
        :config="konvaImageConfig"
        :src="image"
        @click="onClick"
    />
</template>

<script>

import html2canvas from 'html2canvas';
import KonvaCampaignImage from './KonvaCampaignImage.vue';
import { NOTE_COLOR } from '../utils/editor.constants';

export default {
    components: { KonvaCampaignImage },
    props: {
        cell: {
            type: Object,
            required: true,
        },
        borderColor: {
            type: String,
            default: 'transparent',
        },
        flagsInfo: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['on-click'],
    data() {
        return {
            image: null,
        };
    },

    computed: {
        notesWithToolbarFlag() {
            return this.flagsInfo.addToolbarToNotesEnabled;
        },

        imagesWithCorsIssueFixEnabled() {
            return this.flagsInfo.imagesWithCorsIssueFixEnabled;
        },

        hasCustomStyle() {
            return this.cell.value.notes.includes('<img') || this.cell.value.notes.includes('<svg') || this.cell.value.notes.includes(' style=');
        },

        konvaImageConfig() {
            const stroke = this.borderColor === 'transparent' ? '#e0dac9' : this.borderColor;
            const strokeWidth = this.borderColor === 'transparent' ? 1 : 5;

            if (this.hasCustomStyle && !this.notesWithToolbarFlag) {
                return {
                    width: this.cell.mxGeometry.width,
                    height: this.cell.mxGeometry.height,
                    lineHeight: 1.4,
                    letterSpacing: 0,
                    verticalAlign: 'left',
                    horizontalAlign: 'left',
                    align: 'left',
                    stroke,
                    strokeWidth,
                };
            }

            return {
                width: this.cell.mxGeometry.width,
                height: this.cell.mxGeometry.height,
                stroke,
                strokeWidth,
                shadowForStrokeEnabled: false,
                lineHeight: 1.4,
                letterSpacing: 0,
                fill: '#000',
                cornerRadius: 12,
                verticalAlign: 'left',
                horizontalAlign: 'left',
                align: 'left',
                padding: 5,
            };
        },

        notesColor() {
            return this.cell.value.notesColor || NOTE_COLOR.gray;
        },
    },

    async mounted() {
        await this.generateImage();
    },

    beforeUnmount() {
        this.image = null;
    },

    methods: {
        setStylesToWrapper(wrapper) {
            const targetWidth = this.cell.mxGeometry.width;
            const targetHeight = this.cell.mxGeometry.height;

            if (!this.notesWithToolbarFlag) {
                wrapper.style.width = 'fit-content';

                if (!this.hasCustomStyle) {
                    wrapper.style.width = `${targetWidth}px`;
                    wrapper.style.height = `${targetHeight}px`;
                    wrapper.style.backgroundColor = '#fef8e4';
                    wrapper.style.padding = '8px';
                }
                wrapper.style.fontSize = '14px';
                wrapper.style.fontFamily = 'Proxima Nova';
                wrapper.style.lineHeight = 1.5;
                wrapper.style.letterSpacing = 0;
                wrapper.style.position = 'fixed';
                wrapper.style.top = '0px';
                wrapper.style.left = '0px';
                wrapper.style.zIndex = '-50000';
            } else {
                wrapper.style.width = `${targetWidth}px`;
                wrapper.style.height = `${targetHeight}px`;
                wrapper.style.backgroundColor = this.notesWithToolbarFlag ? this.notesColor : '#fef8e4';
                wrapper.style.padding = '8px';
                wrapper.style.fontSize = '14px';
                wrapper.style.fontFamily = '"Sul Sans", Helvetica, Arial, sans-serif';
                wrapper.style.lineHeight = 1.5;
                wrapper.style.letterSpacing = 0;
                wrapper.style.position = 'fixed';
                wrapper.style.top = '0px';
                wrapper.style.left = '0px';
                wrapper.style.overflow = 'hidden';
                wrapper.style.zIndex = '-50000';
                const style = document.createElement('style');

                style.innerHTML = `
                ul, ol {
                    padding-left: 30px;
                    list-style-position: inside;
                }
                ul {
                    list-style-type: disc;
                }
                ol {
                    list-style-type: decimal;
                }
            `;

                wrapper.appendChild(style);
            }
        },

        async generateWrapper() {
            const wrapper = document.createElement('div');

            const mappedHtmlString = this.cell.value.notes?.replaceAll('~br~', '<br />')
                .replaceAll('\n', '<br />');

            this.setStylesToWrapper(wrapper);
            wrapper.insertAdjacentHTML('afterbegin', mappedHtmlString);

            return wrapper;
        },

        async generateImage() {
            const wrapper = await this.generateWrapper();

            document.body.appendChild(wrapper);

            const h2c = html2canvas.default || html2canvas;

            if (this.notesWithToolbarFlag) {
                await this.$nextTick();

                if (this.imagesWithCorsIssueFixEnabled) {
                    await this.verifyCorsIssueForImages(wrapper);
                }

                await this.renderHtml2CanvasImage(wrapper);
            } else {
                // Use html2canvas to render the content
                h2c(wrapper, { logging: false, useCORS: true, allowTaint: true }).then((canvas) => {
                    const dataUrl = canvas.toDataURL('image/png');

                    this.image = dataUrl;
                    // Clean up the temporary wrapper
                    document.body.removeChild(wrapper);
                }).catch((error) => {
                    /* eslint-disable-next-line no-console */
                    console.error('Error rendering image:', error);
                    document.body.removeChild(wrapper); // Ensure cleanup in case of error
                });
            }
        },
        async verifyCorsIssueForImages(wrapper) {
            const images = wrapper.querySelectorAll('img');

            const promises = Array.from(images).map((img) => {
                return new Promise((resolve) => {
                    if (img.src.includes('/proxy-image') || img.src.startsWith('data:') || img.src.startsWith('blob:')) {
                        resolve();

                        return;
                    }

                    fetch(img.src, { method: 'HEAD' })
                        .then(() => {
                            resolve();
                        })
                        .catch(() => {
                            img.src = `/app/automation/images/proxy-image?url=${encodeURIComponent(img.src)}`;
                            resolve();
                        });
                });
            });

            await Promise.all(promises);
        },

        async renderHtml2CanvasImage (wrapper, attempt = 1) {
            try {
                const h2c = html2canvas.default || html2canvas;
                const canvas = await h2c(wrapper, {
                    logging: false,
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                });

                canvas.toBlob((blob) => {
                    if (blob) {
                        this.image = URL.createObjectURL(blob);
                        document.body.removeChild(wrapper);
                    }
                }, 'image/png');
            } catch (error) {
                console.error(`Error capturing image attempt: ${attempt}`, error);

                if (attempt < 2) {
                    setTimeout(() => this.renderHtml2CanvasImage(wrapper, attempt + 1), 100);
                }
            }
        },

        onClick(event) {
            this.$emit('on-click', event);
        },
    },

};
</script>
